import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/toast";
import { AlertCircle } from "lucide-react";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { addFalabella, addRipley } from "../../../../api/integrations";
import { iconList } from "../../../../utils/iconList";
import { cn } from "../../../../utils/cn";
import "../ModernIntegrations.scss";

// Modal genérico "agregar código" para las plataformas que se integran vía
// sender_id (Falabella / Ripley). Un solo componente evita mantener copias
// idénticas como en el modalProvider legacy.
const PLATFORMS = {
  Falabella: { icon: "falabella", chip: "", api: addFalabella },
  Ripley: { icon: "ripley", chip: "mig-logo--dark", api: addRipley },
};

export default function AddCodeModal({ open, platform, onClose, onSuccess }) {
  const { t } = useTranslation();
  const cfg = PLATFORMS[platform];
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const inputId = useId();
  const errorId = useId();
  const inputRef = useRef(null);

  // El foco inicial del Modal cae en el botón de cerrar; lo movemos al campo
  // (es la única acción posible) después de que el primitivo termine su ciclo.
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, [open]);

  if (!cfg) return null;

  const trimmed = code.trim();

  const handleSubmit = async () => {
    if (saving) return;
    if (!trimmed) {
      setFieldError(t("Ingresa el código para continuar."));
      inputRef.current?.focus();
      return;
    }
    setSaving(true);
    setFieldError("");
    try {
      // Payload idéntico al legacy: sender_id con el input tal cual se escribió.
      const res = await cfg.api({ sender_id: code, integrationName: platform });

      if (res?.error?.statusCode === 400) {
        // Caso especial del contrato: código duplicado → error inline en el campo.
        setFieldError(t("Código {{name}} ya existe.", { name: platform }));
      } else if (res && "error" in res) {
        // Error de red/servidor (la API captura y resuelve {error}): toast del contrato.
        console.error(res.error);
        toast.error(t("Ha ocurrido un error al agregar el código de {{name}}.", { name: platform }));
      } else {
        toast.success(t("Código registrado correctamente"));
        onClose();
        if (typeof onSuccess === "function") onSuccess();
      }
    } catch (error) {
      console.error(error?.response || error);
      toast.error(t("Ha ocurrido un error al agregar el código de {{name}}.", { name: platform }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t("Agregar código de {{name}}", { name: platform })}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            disabled={saving || !trimmed}
          >
            {saving ? t("Guardando...") : t("Guardar")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className={cn("mig-logo", cfg.chip)} aria-hidden="true">
            <img src={iconList[cfg.icon]} alt="" className="mig-logo__img dark-safe-img" />
          </span>
          <p className="text-sm text-fg-muted">
            {t(
              "Ingresa el código que identifica tu cuenta de vendedor en {{name}}. Tus pedidos se crearán automáticamente en Beartrack.",
              { name: platform },
            )}
          </p>
        </div>

        <div className="space-y-1.5">
          <FieldLabel htmlFor={inputId}>{t("Código {{name}}", { name: platform })}</FieldLabel>
          <Input
            id={inputId}
            ref={inputRef}
            value={code}
            placeholder={t("Ingresa el código")}
            autoComplete="off"
            invalid={!!fieldError}
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={fieldError ? errorId : undefined}
            onChange={(e) => {
              setCode(e.target.value);
              if (fieldError) setFieldError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !saving) handleSubmit();
            }}
          />
          <p
            id={errorId}
            aria-live="polite"
            className="flex min-h-[1rem] items-center gap-1 text-xs text-status-error"
          >
            {fieldError && (
              <>
                <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {fieldError}
              </>
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
}
